/**
 * API Client for backend communication
 * Handles authentication, request/response interceptors, and API calls
 */

import { supabase } from './supabase';
import { API_CONFIG } from './apiConfig';
import { API_ENDPOINTS, buildUrlWithParams } from './apiEndpoints';
import { createApiError, NetworkError } from './errors';

/**
 * @typedef {Object} RequestOptions
 * @property {string} [method] - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @property {Object} [headers] - Additional headers
 * @property {*} [body] - Request body
 * @property {number} [timeout] - Request timeout in milliseconds
 * @property {boolean} [skipAuth] - Skip authentication header
 * @property {AbortSignal} [signal] - Abort signal for cancellation
 */

/**
 * API Client class
 * Provides methods for making authenticated HTTP requests to the backend
 */
class ApiClient {
  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
    this.defaultHeaders = API_CONFIG.DEFAULT_HEADERS;
    this.requestInterceptors = [];
    this.responseInterceptors = [];
  }

  /**
   * Get authentication token from Supabase
   * @returns {Promise<string|null>} Access token or null
   * @private
   */
  async getAuthToken() {
    try {
      console.log('API: Getting auth token...');

      // Fast path: read session from localStorage and use it if not expired.
      // Key format: sb-{project-ref}-auth-token
      const authTokenKey = Object.keys(localStorage).find(key => key.startsWith('sb-') && key.endsWith('-auth-token'));
      const storedSession = authTokenKey ? localStorage.getItem(authTokenKey) : null;
      let parsedSession = null;
      if (storedSession) {
        try {
          parsedSession = JSON.parse(storedSession);
          const accessToken = parsedSession?.access_token;
          const expiresAt = parsedSession?.expires_at;

          // Valid (with 60s buffer) — use as-is
          if (accessToken && expiresAt && (expiresAt * 1000) > (Date.now() + 60000)) {
            console.log('API: Using cached token from localStorage');
            return accessToken;
          }
        } catch (parseError) {
          console.warn('API: Failed to parse stored session:', parseError);
        }
      }

      // Token is expired/near-expiry. Proactively refresh rather than letting
      // an expired token hit the backend (getSession() does NOT refresh).
      if (parsedSession?.refresh_token) {
        console.log('API: Cached token expired, refreshing session...');
        const { data: { session }, error } = await this._refreshSession();
        if (error) {
          console.error('API: Proactive refresh failed:', error.message);
        }
        if (session?.access_token) {
          return session.access_token;
        }
      }

      // Last resort: ask Supabase (may return null if not signed in)
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token || null;
    } catch (error) {
      console.error('API: Error getting auth token:', error.message);
      return null;
    }
  }

  /**
   * Refresh the Supabase session, deduplicating concurrent callers so we
   * don't fire multiple refresh requests in parallel.
   * @returns {Promise<{data: {session: Object|null}, error: Error|null}>}
   * @private
   */
  _refreshSession() {
    if (!this._refreshPromise) {
      this._refreshPromise = supabase.auth.refreshSession().finally(() => {
        this._refreshPromise = null;
      });
    }
    return this._refreshPromise;
  }

  /**
   * Build complete URL with base URL and endpoint
   * @param {string} endpoint - API endpoint
   * @returns {string} Complete URL
   * @private
   */
  buildUrl(endpoint) {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${this.baseUrl}${cleanEndpoint}`;
  }

  /**
   * Build request headers
   * @param {Object} additionalHeaders - Additional headers to merge
   * @param {boolean} skipAuth - Skip adding auth token
   * @returns {Promise<Object>} Headers object
   * @private
   */
  async buildHeaders(additionalHeaders = {}, skipAuth = false) {
    const headers = {
      ...this.defaultHeaders,
      ...additionalHeaders,
    };

    if (!skipAuth) {
      const token = await this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Create abort controller with timeout
   * @param {number} timeout - Timeout in milliseconds
   * @returns {AbortController} Abort controller
   * @private
   */
  createAbortController(timeout) {
    const controller = new AbortController();

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    // Clear timeout when request completes
    controller.signal.addEventListener('abort', () => {
      clearTimeout(timeoutId);
    });

    return controller;
  }

  /**
   * Handle fetch errors
   * @param {Error} error - Fetch error
   * @throws {NetworkError|ApiError}
   * @private
   */
  handleFetchError(error) {
    if (error.name === 'AbortError') {
      throw new NetworkError('Request timeout', { originalError: error });
    }

    if (!navigator.onLine) {
      throw new NetworkError('No internet connection', { originalError: error });
    }

    throw new NetworkError('Network request failed', { originalError: error });
  }

  /**
   * Parse response body
   * @param {Response} response - Fetch response
   * @returns {Promise<*>} Parsed response body
   * @private
   */
  async parseResponse(response) {
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      return response.json();
    }

    if (contentType?.includes('text/')) {
      return response.text();
    }

    return response.blob();
  }

  /**
   * Handle HTTP error responses
   * @param {Response} response - Fetch response
   * @throws {ApiError}
   * @private
   */
  async handleErrorResponse(response) {
    let errorData;

    try {
      errorData = await this.parseResponse(response);
    } catch {
      errorData = { detail: 'An error occurred' };
    }

    const message = errorData?.detail || errorData?.message || `HTTP error! status: ${response.status}`;
    const details = errorData?.errors || errorData;

    throw createApiError(response.status, message, details);
  }

  /**
   * Core request method
   * @param {string} endpoint - API endpoint
   * @param {RequestOptions} options - Request options
   * @returns {Promise<*>} Response data
   * @throws {ApiError|NetworkError}
   */
  async request(endpoint, options = {}, isRetry = false) {
    const {
      method = 'GET',
      headers: additionalHeaders = {},
      body,
      timeout = this.timeout,
      skipAuth = false,
      signal,
    } = options;

    try {
      console.log('API: Starting request to', endpoint);
      const url = this.buildUrl(endpoint);
      console.log('API: Built URL:', url);
      const headers = await this.buildHeaders(additionalHeaders, skipAuth);
      console.log('API: Headers built, making fetch...');

      // Create abort controller for timeout if no signal provided
      const abortController = signal ? null : this.createAbortController(timeout);
      const finalSignal = signal || abortController?.signal;

      const fetchOptions = {
        method,
        headers,
        signal: finalSignal,
      };

      // Add body for non-GET requests
      if (body && method !== 'GET' && method !== 'HEAD') {
        if (body instanceof FormData) {
          // Remove Content-Type for FormData (browser sets it with boundary)
          delete fetchOptions.headers['Content-Type'];
          fetchOptions.body = body;
        } else if (typeof body === 'object') {
          fetchOptions.body = JSON.stringify(body);
        } else {
          fetchOptions.body = body;
        }
      }

      // Execute request interceptors
      let modifiedOptions = fetchOptions;
      for (const interceptor of this.requestInterceptors) {
        modifiedOptions = await interceptor(url, modifiedOptions);
      }

      // Make the request
      const response = await fetch(url, modifiedOptions);

      // Handle 401 errors with retry after session refresh
      if (response.status === 401 && !isRetry && !skipAuth) {
        console.log('API: 401 received, refreshing session and retrying...');
        try {
          await this._refreshSession();
          return this.request(endpoint, options, true);
        } catch (refreshError) {
          console.error('API: Session refresh failed:', refreshError);
        }
      }

      // Handle error responses
      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      // Parse response
      const data = await this.parseResponse(response);

      // Execute response interceptors
      let modifiedData = data;
      for (const interceptor of this.responseInterceptors) {
        modifiedData = await interceptor(response, modifiedData);
      }

      return modifiedData;
    } catch (error) {
      // Handle network errors
      if (error.name === 'TypeError' || error.name === 'AbortError') {
        this.handleFetchError(error);
      }

      // Re-throw API errors
      throw error;
    }
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @param {RequestOptions} options - Additional options
   * @returns {Promise<*>} Response data
   */
  async get(endpoint, params = {}, options = {}) {
    console.log('GET params:', params, 'keys:', Object.keys(params));
    const url = Object.keys(params).length > 0 ? buildUrlWithParams(endpoint, params) : endpoint;
    console.log('GET url:', url);
    return this.request(url, { ...options, method: 'GET' });
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {*} body - Request body
   * @param {RequestOptions} options - Additional options
   * @returns {Promise<*>} Response data
   */
  async post(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {*} body - Request body
   * @param {RequestOptions} options - Additional options
   * @returns {Promise<*>} Response data
   */
  async put(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {*} body - Request body
   * @param {RequestOptions} options - Additional options
   * @returns {Promise<*>} Response data
   */
  async patch(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'PATCH', body });
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {RequestOptions} options - Additional options
   * @returns {Promise<*>} Response data
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Add request interceptor
   * @param {Function} interceptor - Interceptor function
   */
  addRequestInterceptor(interceptor) {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Add response interceptor
   * @param {Function} interceptor - Interceptor function
   */
  addResponseInterceptor(interceptor) {
    this.responseInterceptors.push(interceptor);
  }

  // ========================================
  // User API Methods
  // ========================================

  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Created user
   */
  async registerUser(userData) {
    return this.post(API_ENDPOINTS.USERS.REGISTER, userData);
  }

  /**
   * Get user by ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User data
   */
  async getUser(userId) {
    return await this.get(API_ENDPOINTS.USERS.BY_ID(userId));
  }

  /**
   * Get user by user ID (new endpoint)
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User data
   */
  async getUserByUserId(userId) {
    return await this.get(API_ENDPOINTS.USERS.BY_USER_ID(userId));
  }

  /**
   * Update user
   * @param {string} userId - User ID
   * @param {Object} userData - Updated user data
   * @returns {Promise<Object>} Updated user
   */
  async updateUser(userId, userData) {
    return this.patch(API_ENDPOINTS.USERS.BY_ID(userId), userData);
  }

  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile
   */
  async getUserProfile() {
    return this.get(API_ENDPOINTS.USERS.PROFILE);
  }

  // ========================================
  // Operator API Methods
  // ========================================

  /**
   * Get list of operators
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @param {Object} filters - Optional filters (name, dba_name, certificate_number, etc.)
   * @returns {Promise<Array>} List of operators
   */
  async getOperators(skip = 0, limit = 100, filters = {}) {
    return this.get(API_ENDPOINTS.OPERATORS.BASE, { skip, limit, ...filters });
  }

  /**
   * Get operator by ID
   * @param {string} operatorId - Operator ID
   * @param {Object} params - Optional query parameters (e.g., { include: 'ntsb_incidents,ucc_verifications' })
   * @returns {Promise<Object>} Operator data
   */
  async getOperatorById(operatorId, params = {}) {
    console.log('getOperatorById called with:', operatorId, params);
    return this.get(API_ENDPOINTS.OPERATORS.BY_ID(operatorId), params);
  }

  /**
   * Create new operator
   * @param {Object} operatorData - Operator data
   * @returns {Promise<Object>} Created operator
   */
  async createOperator(operatorData) {
    return this.post(API_ENDPOINTS.OPERATORS.BASE, operatorData);
  }

  /**
   * Update operator
   * @param {string} operatorId - Operator ID
   * @param {Object} operatorData - Updated operator data
   * @returns {Promise<Object>} Updated operator
   */
  async updateOperator(operatorId, operatorData) {
    return this.put(API_ENDPOINTS.OPERATORS.BY_ID(operatorId), operatorData);
  }

  /**
   * Delete operator
   * @param {string} operatorId - Operator ID
   * @returns {Promise<void>}
   */
  async deleteOperator(operatorId) {
    return this.delete(API_ENDPOINTS.OPERATORS.BY_ID(operatorId));
  }

  /**
   * Search operators
   * @param {string} query - Search query
   * @returns {Promise<Array>} Search results
   */
  async searchOperators(query) {
    return this.get(API_ENDPOINTS.OPERATORS.SEARCH, { q: query });
  }

  // ========================================
  // Aircraft API Methods
  // ========================================

  /**
   * Get list of aircraft
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @param {Object} filters - Optional filters (tail_number, operator_id, make_model, etc.)
   * @returns {Promise<Array>} List of aircraft
   */
  async getAircrafts(skip = 0, limit = 100, filters = {}) {
    return this.get(API_ENDPOINTS.AIRCRAFT.BASE, { skip, limit, ...filters });
  }

  /**
   * Get aircraft by ID
   * @param {string} aircraftId - Aircraft ID or make_model
   * @param {Object} params - Optional parameters
   * @param {string} params.filter - Filter type (e.g., 'make_model')
   * @returns {Promise<Object>} Aircraft data
   */
  async getAircraftById(aircraftId, params = {}) {
    return this.get(API_ENDPOINTS.AIRCRAFT.BY_ID(aircraftId), params);
  }

  /**
   * Get aircraft amenities
   * @param {string} aircraftId - Aircraft ID
   * @returns {Promise<Object>} Aircraft amenities data
   */
  async getAircraftAmenities(aircraftId) {
    return this.get(API_ENDPOINTS.AIRCRAFT.AMENITIES(aircraftId));
  }

  /**
   * Get aircraft photos
   * @param {string} aircraftId - Aircraft ID
   * @returns {Promise<Array>} Aircraft photos data
   */
  async getAircraftPhotos(aircraftId) {
    return this.get(API_ENDPOINTS.AIRCRAFT.PHOTOS(aircraftId));
  }

  /**
   * Create new aircraft
   * @param {Object} aircraftData - Aircraft data
   * @returns {Promise<Object>} Created aircraft
   */
  async createAircraft(aircraftData) {
    return this.post(API_ENDPOINTS.AIRCRAFT.BASE, aircraftData);
  }

  /**
   * Update aircraft
   * @param {string} aircraftId - Aircraft ID
   * @param {Object} aircraftData - Updated aircraft data
   * @returns {Promise<Object>} Updated aircraft
   */
  async updateAircraft(aircraftId, aircraftData) {
    return this.put(API_ENDPOINTS.AIRCRAFT.BY_ID(aircraftId), aircraftData);
  }

  /**
   * Delete aircraft
   * @param {string} aircraftId - Aircraft ID
   * @returns {Promise<void>}
   */
  async deleteAircraft(aircraftId) {
    return this.delete(API_ENDPOINTS.AIRCRAFT.BY_ID(aircraftId));
  }

  /**
   * Search aircraft by tail number
   * @param {string} tailNumber - Aircraft tail number (partial match, case-insensitive)
   * @returns {Promise<Array>} Aircraft data
   */
  async searchAircraftByTail(tailNumber) {
    return this.get(API_ENDPOINTS.AIRCRAFT.BASE, { tail_number: tailNumber, include: 'operator,features,photos,make_model' });
  }

  // ========================================
  // JetNet API Methods
  // ========================================

  /**
   * Search JetNet for aircraft by tail number
   * @param {string} tailNumber - Aircraft tail number
   * @returns {Promise<Array>} Aircraft data from JetNet
   */
  async searchJetNetAircraft(tailNumber) {
    return this.get(API_ENDPOINTS.JETNET.AIRCRAFTS, { tail_number: tailNumber });
  }

  /**
   * Create/import a JetNet aircraft record
   * @param {Object} aircraftData - Aircraft object from JetNet search
   * @returns {Promise<Object>} Created aircraft data
   */
  async createJetNetAircraft(aircraftData) {
    return this.post(API_ENDPOINTS.JETNET.AIRCRAFTS, aircraftData);
  }

  // ========================================
  // Organization API Methods
  // ========================================

  /**
   * Get list of organizations
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @returns {Promise<Array>} List of organizations
   */
  async getOrganizations(skip = 0, limit = 100) {
    return this.get(API_ENDPOINTS.ORGANIZATIONS.BASE, { skip, limit });
  }

  /**
   * Get organization by ID
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Organization data
   */
  async getOrganizationById(organizationId) {
    return this.get(API_ENDPOINTS.ORGANIZATIONS.BY_ID(organizationId));
  }

  /**
   * Get organization by ID using checkout token (for public access)
   * @param {string} organizationId - Organization ID
   * @param {string} token - Checkout token for authentication
   * @returns {Promise<Object>} Organization data
   */
  async getOrganizationByIdWithToken(organizationId, token) {
    return this.request(
      API_ENDPOINTS.ORGANIZATIONS.BY_ID(organizationId),
      {
        method: 'GET',
        headers: { 'X-Checkout-Token': token }
      }
    );
  }

  /**
   * Create new organization
   * @param {Object} organizationData - Organization data
   * @returns {Promise<Object>} Created organization
   */
  async createOrganization(organizationData) {
    return this.post(API_ENDPOINTS.ORGANIZATIONS.BASE, organizationData);
  }

  /**
   * Update organization
   * @param {string} organizationId - Organization ID
   * @param {Object} organizationData - Updated organization data
   * @returns {Promise<Object>} Updated organization
   */
  async updateOrganization(organizationId, organizationData) {
    return this.put(API_ENDPOINTS.ORGANIZATIONS.BY_ID(organizationId), organizationData);
  }

  /**
   * Delete organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<void>}
   */
  async deleteOrganization(organizationId) {
    return this.delete(API_ENDPOINTS.ORGANIZATIONS.BY_ID(organizationId));
  }

  // ========================================
  // Upload Methods
  // ========================================

  /**
   * Upload a file
   * @param {File} file - File object to upload
   * @param {string} path - Upload destination path
   * @returns {Promise<Object>} Upload result
   */
  async uploadFile(file, path) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path);
    return this.post(API_ENDPOINTS.UPLOADS.BASE, formData);
  }

  // ========================================
  // LLM API Methods
  // ========================================

  /**
   * Get estimated arrival time for a flight
   * @param {Object} params - { from_location, to_location, departure_time }
   * @returns {Promise<Object>} { from_location, to_location, departure_time, arrival_time }
   */
  async getArrivalTime(params) {
    return this.post(API_ENDPOINTS.LLM.ARRIVAL_TIME, params);
  }

  // ========================================
  // Stripe Connect API Methods
  // ========================================

  /**
   * Get Stripe Connected Account status for an organization
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object>} Stripe account status
   */
  async getStripeAccountStatus(orgId) {
    return this.get(API_ENDPOINTS.STRIPE.CONNECTED_ACCOUNT(orgId));
  }

  /**
   * Start Stripe onboarding for an organization
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object>} Onboarding details with URL
   */
  async startStripeOnboarding(orgId) {
    return this.post(API_ENDPOINTS.STRIPE.ONBOARDING_START(orgId));
  }

  /**
   * Create a Stripe payment link
   * @param {Object} payload - Payment link details
   * @returns {Promise<Object>} Payment link response with URL
   */
  async createPaymentLink(payload) {
    return this.post(API_ENDPOINTS.STRIPE.PAYMENT_LINKS, payload);
  }

  /**
   * Create an MX Merchant payment link
   * @param {Object} payload - Payment link details
   * @returns {Promise<Object>} Payment link response with URL
   */
  async createMxMerchantPaymentLink(payload, checkoutToken = null) {
    if (checkoutToken) {
      return this.post(API_ENDPOINTS.MXMERCHANT.PAYMENT_LINKS, payload, {
        headers: { 'X-Checkout-Token': checkoutToken },
        skipAuth: true
      });
    }
    return this.post(API_ENDPOINTS.MXMERCHANT.PAYMENT_LINKS, payload);
  }

  /**
   * Capture an MX Merchant payment transaction
   * @param {Object} payload - Capture details (payment_id, transId, refNum)
   * @param {string} [checkoutToken] - Optional checkout token for public access
   * @returns {Promise<Object>} Capture response
   */
  async captureMxMerchantPayment(payload, checkoutToken = null) {
    if (checkoutToken) {
      return this.patch(API_ENDPOINTS.MXMERCHANT.CAPTURE_PAYMENT, payload, {
        headers: { 'X-Checkout-Token': checkoutToken },
        skipAuth: true
      });
    }
    return this.patch(API_ENDPOINTS.MXMERCHANT.CAPTURE_PAYMENT, payload);
  }

  /**
   * Capture a Priority Commerce (Passport) checkout payment.
   * @param {Object} payload - Capture details
   * @param {string} [checkoutToken] - Optional checkout token for public access
   * @returns {Promise<Object>} Capture response
   */
  async capturePassportPayment(payload, checkoutToken = null) {
    if (checkoutToken) {
      return this.post(API_ENDPOINTS.PASSPORT.CAPTURE_PAYMENT, payload, {
        headers: { 'X-Checkout-Token': checkoutToken },
        skipAuth: true
      });
    }
    return this.post(API_ENDPOINTS.PASSPORT.CAPTURE_PAYMENT, payload);
  }

  /**
   * Fetch Priority Commerce client secrets for the wire/ACH payment widget.
   * @param {Object} payload
   * @param {string|null} checkoutToken - public checkout token (token-based flow)
   * @returns {Promise<Object>} { client_secret, business_id, ... }
   */
  async getClientSecrets(payload = {}, checkoutToken = null) {
    if (checkoutToken) {
      return this.post(API_ENDPOINTS.CLIENTS.CLIENT_SECRETS, payload, {
        headers: { 'X-Checkout-Token': checkoutToken },
        skipAuth: true
      });
    }
    return this.post(API_ENDPOINTS.CLIENTS.CLIENT_SECRETS, payload);
  }

  // ========================================
  // Bids API Methods
  // ========================================

  /**
   * Get list of bids
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @param {Object} filters - Optional filters (status, client_id, etc.)
   * @returns {Promise<Array>} List of bids
   */
  async getBids(skip = 0, limit = 100, filters = {}) {
    return this.get(API_ENDPOINTS.BIDS.BASE, { ...filters });
  }

  /**
   * Get bid by ID
   * @param {string} bidId - Bid ID
   * @returns {Promise<Object>} Bid data
   */
  async getBidById(bidId) {
    return this.get(API_ENDPOINTS.BIDS.BY_ID(bidId));
  }

  /**
   * Create new bid
   * @param {Object} bidData - Bid data
   * @returns {Promise<Object>} Created bid
   */
  async createBid(bidData) {
    return this.post(API_ENDPOINTS.BIDS.BASE, bidData);
  }

  /**
   * Update bid
   * @param {string} bidId - Bid ID
   * @param {Object} bidData - Updated bid data
   * @returns {Promise<Object>} Updated bid
   */
  async updateBid(bidId, bidData) {
    return this.put(API_ENDPOINTS.BIDS.BY_ID(bidId), bidData);
  }

  /**
   * Delete bid
   * @param {string} bidId - Bid ID
   * @returns {Promise<void>}
   */
  async deleteBid(bidId) {
    return this.delete(API_ENDPOINTS.BIDS.BY_ID(bidId));
  }

  /**
   * Get shortlisted bids
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @returns {Promise<Array>} Shortlisted bids
   */
  async getShortlistedBids(skip = 0, limit = 100) {
    return this.get(API_ENDPOINTS.BIDS.SHORTLIST, { skip, limit });
  }

  /**
   * Get list of bids options
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @param {Object} filters - Optional filters (status, client_id, etc.)
   * @returns {Promise<Array>} List of bids options
   */
  async getBidsOptions(skip = 0, limit = 100, filters = {}) {
    return this.get(API_ENDPOINTS.BIDS.OPTIONS, { skip, limit, ...filters });
  }

  /**
   * Create bid option
   * @param {Object} bidOptionData - Bid option data
   * @returns {Promise<Object>} Created bid option
   */
  async createBidOption(bidOptionData) {
    return this.post(API_ENDPOINTS.BIDS.OPTIONS, bidOptionData);
  }

  /**
   * Update bid option
   * @param {string} bidOptionId - Bid option ID
   * @param {Object} data - Bid option data
   * @returns {Promise<Object>} Updated bid option
   */
  async updateBidOption(bidOptionId, data) {
    return this.put(API_ENDPOINTS.BIDS.OPTIONS_BY_ID(bidOptionId), data);
  }

  /**
   * Get bid option by ID
   * @param {string} bidOptionId - Bid option ID
   * @returns {Promise<Object>} Bid option data
   */
  async getBidOptionById(bidOptionId) {
    return this.get(API_ENDPOINTS.BIDS.OPTIONS_BY_ID(bidOptionId));
  }

  async getBidOptionByIdWithToken(bidOptionId, token) {
    return this.request(API_ENDPOINTS.BIDS.OPTIONS_BY_ID(bidOptionId), {
      method: 'GET',
      headers: { 'X-Checkout-Token': token }
    });
  }

  /**
   * Get bid by bid number
   * @param {string} bidNumber - Bid number
   * @returns {Promise<Object>} Bid data
   */
  async getBidByNumber(bidNumber) {
    return this.get(API_ENDPOINTS.BIDS.BY_NUMBER(bidNumber));
  }

  /**
   * Get bids by broker organization
   * @param {string} brokerOrgId - Broker organization ID
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @returns {Promise<Array>} List of bids
   */
  async getBidsByBroker(brokerOrgId, skip = 0, limit = 100) {
    return this.get(API_ENDPOINTS.BIDS.BY_BROKER(brokerOrgId), { skip, limit });
  }

  /**
   * Get bids by status
   * @param {string} status - Bid status (DRAFT, SENT, ACCEPTED, REJECTED)
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @returns {Promise<Array>} List of bids
   */
  async getBidsByStatus(status, skip = 0, limit = 100) {
    return this.get(API_ENDPOINTS.BIDS.BY_STATUS(status), { skip, limit });
  }

  /**
   * Toggle bid shortlist status
   * @param {string} bidId - Bid ID
   * @param {boolean} shortlisted - Whether to shortlist or un-shortlist
   * @returns {Promise<Object>} Updated bid
   */
  async toggleBidShortlist(bidId, shortlisted) {
    return this.put(API_ENDPOINTS.BIDS.BY_ID(bidId), { shortlisted });
  }

  // ==========================================================================
  // Message API Methods
  // ==========================================================================

  /**
   * Get all messages with optional filters
   * @param {number} skip - Number of messages to skip (pagination)
   * @param {number} limit - Maximum number of messages to return
   * @param {Object} filters - Optional filters (message_type, from_email, to_email, subject, etc.)
   * @returns {Promise<Array>} List of messages
   */
  async getMessages(skip = 0, limit = 100, filters = {}) {
    return this.get(API_ENDPOINTS.MESSAGES.BASE, { skip, limit, ...filters });
  }

  /**
   * Get message by ID
   * @param {string} messageId - Message ID
   * @returns {Promise<Object>} Message object
   */
  async getMessageById(messageId) {
    return this.get(API_ENDPOINTS.MESSAGES.BY_ID(messageId));
  }

  /**
   * Get unprocessed messages
   * @param {number} limit - Maximum number of messages to return
   * @returns {Promise<Array>} List of unprocessed messages
   */
  async getUnprocessedMessages(limit = 100) {
    return this.get(API_ENDPOINTS.MESSAGES.UNPROCESSED, { limit });
  }

  /**
   * Update a message
   * @param {string} messageId - Message ID
   * @param {Object} updateData - Update data (subject, from_name, processed, etc.)
   * @returns {Promise<Object>} Updated message object
   */
  async updateMessage(messageId, updateData) {
    return this.put(API_ENDPOINTS.MESSAGES.BY_ID(messageId), updateData);
  }

  /**
   * Mark message as processed
   * @param {string} messageId - Message ID
   * @returns {Promise<Object>} Updated message object
   */
  async markMessageProcessed(messageId) {
    return this.post(API_ENDPOINTS.MESSAGES.MARK_PROCESSED(messageId), {});
  }

  // ==================== Quotes ====================

  /**
   * Create a new quote
   * @param {Object} quoteData - Quote data
   * @returns {Promise<Object>} Created quote object
   */
  async createQuote(quoteData) {
    return this.post(API_ENDPOINTS.QUOTES.BASE, quoteData);
  }

  /**
   * Get all quotes
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @param {Object} filters - Optional filters (e.g., { status: 'sent' })
   * @returns {Promise<Array>} List of quotes
   */
  async getQuotes(skip = 0, limit = 100, filters = {}) {
    return this.get(API_ENDPOINTS.QUOTES.BASE, {
      skip,
      limit,
      include: 'bid_items,bid_items.bid_options,bid_items.bid_options.aircraft,bid_items.bid_options.operator,client',
      ...filters
    });
  }

  /**
   * Get user's bids for track page (optimized endpoint)
   * @param {number} skip - Number of bids to skip
   * @param {number} limit - Maximum number of bids to return
   * @param {Object} filters - Optional filters (departure_date_from, departure_date_to, status, sort_by, sort_order)
   * @returns {Promise<Array>} List of bids with bid_options and aircraft only
   */
  async getMyTripsBids(skip = 0, limit = 10, filters = {}) {
    return this.get(API_ENDPOINTS.MY_TRIPS.BIDS, {
      skip,
      limit,
      ...filters
    });
  }

  /**
   * Get quote by ID
   * @param {string} quoteId - Quote ID
   * @returns {Promise<Object>} Quote object
   */
  async getQuoteById(quoteId) {
    return this.get(API_ENDPOINTS.QUOTES.BY_ID(quoteId)+'?include=bid_items,bid_items.bid_options,bid_items.bid_options.aircraft,bid_items.bid_options.operator,client');
  }

  /**
   * Get quote by ID using checkout token (for public access)
   * @param {string} quoteId - Quote ID
   * @param {string} token - Checkout token for authentication
   * @returns {Promise<Object>} Quote object
   */
  async getQuoteByIdWithToken(quoteId, token) {
    return this.request(
      API_ENDPOINTS.QUOTES.BY_ID(quoteId)+'?include=bid_items,bid_items.bid_options,bid_items.bid_options.aircraft,bid_items.bid_options.operator,client',
      {
        method: 'GET',
        headers: { 'X-Checkout-Token': token }
      }
    );
  }

  /**
   * Update a quote
   * @param {string} quoteId - Quote ID
   * @param {Object} updateData - Updated quote data
   * @param {string} [checkoutToken] - Optional checkout token for public access
   * @returns {Promise<Object>} Updated quote object
   */
  async updateQuote(quoteId, updateData, checkoutToken = null) {
    if (checkoutToken) {
      return this.put(API_ENDPOINTS.QUOTES.BY_ID(quoteId), updateData, {
        headers: { 'X-Checkout-Token': checkoutToken }
      });
    }
    return this.put(API_ENDPOINTS.QUOTES.BY_ID(quoteId), updateData);
  }

  async getQuotePassengers(quoteId) {
    return this.get(API_ENDPOINTS.QUOTES.PASSENGERS(quoteId));
  }

  async createQuotePassengersBatch(quoteId, passengers) {
    return this.post(API_ENDPOINTS.QUOTES.PASSENGERS_BATCH(quoteId), passengers);
  }

  // ==================== Clients ====================

  /**
   * Get all clients
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @param {Object} filters - Optional filters (name, email, etc.)
   * @returns {Promise<Array>} List of clients
   */
  async getClients(skip = 0, limit = 100, filters = {}) {
    return this.get(API_ENDPOINTS.CLIENTS.BASE, { skip, limit, ...filters });
  }

  /**
   * Create a new client
   * @param {Object} clientData - Client fields
   * @returns {Promise<Object>} Created client object
   */
  async createClient(clientData) {
    return this.post(API_ENDPOINTS.CLIENTS.BASE, clientData);
  }

  /**
   * Get client by ID
   * @param {string} clientId - Client ID
   * @returns {Promise<Object>} Client object
   */
  async getClientById(clientId, params = {}) {
    return this.get(API_ENDPOINTS.CLIENTS.BY_ID(clientId), params);
  }

  /**
   * Update a client
   * @param {string} clientId - Client ID
   * @param {Object} clientData - Updated client fields
   * @returns {Promise<Object>} Updated client object
   */
  async updateClient(clientId, clientData) {
    return this.patch(API_ENDPOINTS.CLIENTS.BY_ID(clientId), clientData);
  }

  /**
   * Update a client's Priority Commerce vault info (customer/card IDs returned
   * by the PAYMENT_METHOD_CAPTURE widget).
   * @param {string} clientId - Client ID
   * @param {Object} vaultData - Vault payload returned from the capture widget
   * @param {string|null} checkoutToken - public checkout token (token-based flow)
   * @returns {Promise<Object>} Updated client object
   */
  async updateClientPriorityVault(clientId, vaultData, checkoutToken = null) {
    if (checkoutToken) {
      return this.patch(API_ENDPOINTS.CLIENTS.PRIORITY_VAULT(clientId), vaultData, {
        headers: { 'X-Checkout-Token': checkoutToken },
        skipAuth: true
      });
    }
    return this.patch(API_ENDPOINTS.CLIENTS.PRIORITY_VAULT(clientId), vaultData);
  }

  async getClientPassengers(clientId) {
    return this.get(API_ENDPOINTS.CLIENTS.PASSENGERS(clientId));
  }

  async createClientPassengersBatch(clientId, passengers) {
    return this.post(API_ENDPOINTS.CLIENTS.PASSENGERS_BATCH(clientId), passengers);
  }

  async updateClientPassenger(passengerId, data) {
    return this.put(API_ENDPOINTS.CLIENT_PASSENGERS.BY_ID(passengerId), data);
  }

  async deleteClientPassenger(passengerId) {
    return this.delete(API_ENDPOINTS.CLIENT_PASSENGERS.BY_ID(passengerId));
  }

  async deleteQuotePassenger(passengerId) {
    return this.delete(API_ENDPOINTS.QUOTE_PASSENGERS.BY_ID(passengerId));
  }

  /**
   * Get all airports
   * @param {number} limit - Maximum number of airports to return
   * @returns {Promise<Array>} List of airports
   */
  async getAirports(limit = 10000) {
    const url = limit ? `${API_ENDPOINTS.AIRPORTS.BASE}?limit=${limit}` : API_ENDPOINTS.AIRPORTS.BASE;
    return this.get(url);
  }

  async getAirportByIcao(icao) {
    const data = await this.get(`${API_ENDPOINTS.AIRPORTS.BASE}?icao=${icao}`);
    return Array.isArray(data) ? data[0] || null : data;
  }

  /**
   * Send a message/email
   * @param {Object} messageData - Message data to send
   * @param {string} checkoutToken - Optional checkout token for public access
   * @returns {Promise<Object>} Sent message response
   */
  async sendMessage(messageData, checkoutToken = null) {
    if (checkoutToken) {
      return this.post(API_ENDPOINTS.MESSAGES.SEND, messageData, {
        headers: { 'X-Checkout-Token': checkoutToken },
        skipAuth: true
      });
    }
    return this.post(API_ENDPOINTS.MESSAGES.SEND, messageData);
  }

  // ==================== Checkout ====================

  /**
   * Create a checkout token
   * @param {Object} tokenData - Token data for checkout
   * @returns {Promise<Object>} Checkout token response
   */
  async createCheckoutToken(tokenData) {
    return this.post(API_ENDPOINTS.CHECKOUT.TOKEN, tokenData);
  }

  /**
   * Create a tracking token for public flight tracking access
   * @param {string} quoteId - Quote ID
   * @param {number} legNumber - Leg number
   * @returns {Promise<Object>} Tracking token response
   */
  async createTrackingToken(quoteId, legNumber) {
    return this.post(`${API_ENDPOINTS.FLIGHTRADAR24.TOKEN}?quote_id=${encodeURIComponent(quoteId)}&leg_number=${encodeURIComponent(legNumber)}`);
  }

  /**
   * Create a payment intent for Stripe checkout
   * @param {Object} paymentData - Payment intent data
   * @param {string} paymentData.amount - Amount to charge
   * @param {string} paymentData.currency - Currency code (e.g., 'USD')
   * @param {string} paymentData.quote_id - Quote ID
   * @param {string} paymentData.description - Payment description
   * @param {string} [checkoutToken] - Optional checkout token for public access
   * @returns {Promise<Object>} Payment intent response with client_secret
   */
  /**
   * Send an invoice via Mercury
   * @param {Object} invoiceData - Invoice data
   * @returns {Promise<Object>} Invoice response
   */
  async sendInvoice(invoiceData) {
    return this.post(API_ENDPOINTS.MERCURY.INVOICE, invoiceData);
  }

  // ========================================
  // FlightRadar24 API Methods
  // ========================================

  // Flights
  async getFlights(filters = {}) {
    return this.get(API_ENDPOINTS.FLIGHTS.BASE, filters);
  }

  async createFlight(flightData) {
    return this.post(API_ENDPOINTS.FLIGHTS.BASE, flightData);
  }

  async getFlightById(flightId) {
    return this.get(API_ENDPOINTS.FLIGHTS.BY_ID(flightId));
  }

  async updateFlight(flightId, flightData) {
    return this.put(API_ENDPOINTS.FLIGHTS.BY_ID(flightId), flightData);
  }

  async patchFlight(flightId, flightData) {
    return this.patch(API_ENDPOINTS.FLIGHTS.BY_ID(flightId), flightData);
  }

  async createFlightsBatch(flightsData) {
    return this.post(API_ENDPOINTS.FLIGHTS.BATCH, flightsData);
  }

  async updateFlightStatus(quoteId, flightStatus) {
    return this.patch(API_ENDPOINTS.FLIGHTS.BASE, { quote_id: quoteId, flight_status: flightStatus });
  }

  async getLiveFlightPositions(registrations, params = {}) {
    return this.get(API_ENDPOINTS.FLIGHTRADAR24.LIVE_POSITIONS, { registrations, ...params });
  }

  async getFlightSummary(registrations, flightDatetimeFrom, flightDatetimeTo) {
    return this.get(API_ENDPOINTS.FLIGHTRADAR24.FLIGHT_SUMMARY, {
      registrations,
      flight_datetime_from: flightDatetimeFrom,
      flight_datetime_to: flightDatetimeTo,
    });
  }

  async getAirportFull(code, params = {}) {
    return this.get(API_ENDPOINTS.FLIGHTRADAR24.AIRPORT(code), params);
  }

  async getFlightTracks(flightId, params = {}) {
    return this.get(API_ENDPOINTS.FLIGHTRADAR24.FLIGHT_TRACKS, { flight_id: flightId, ...params });
  }

  async createCheckoutSession(sessionData, checkoutToken = null) {
    if (checkoutToken) {
      return this.post(API_ENDPOINTS.CHECKOUT.SESSION, sessionData, {
        headers: { 'X-Checkout-Token': checkoutToken },
        skipAuth: true
      });
    }
    return this.post(API_ENDPOINTS.CHECKOUT.SESSION, sessionData);
  }

  async createPaymentIntent(paymentData, checkoutToken = null) {
    if (checkoutToken) {
      return this.request(API_ENDPOINTS.CHECKOUT.PAYMENTINTENTS, {
        method: 'POST',
        body: JSON.stringify(paymentData),
        headers: { 'X-Checkout-Token': checkoutToken }
      });
    }
    return this.post(API_ENDPOINTS.CHECKOUT.PAYMENTINTENTS, paymentData);
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient();

// Export class for testing purposes
export { ApiClient };
